/**
 * LayoutItem behavior — item sizing inside a Layout container
 *
 * Lightweight behavior for children of a Layout container.
 * Controls how the child sizes (Fixed/Hug/Fill), flex grow/shrink,
 * and absolute positioning. The parent Layout behavior reads this
 * data during tree building via getBehavior(LayoutItem).
 *
 * This is data-only — all layout computation happens in the parent
 * Layout behavior's engine. LayoutItem just marks dirty on commit
 * so the parent re-runs layout.
 */

import { TraceBehavior } from "./tracebehavior";
import type { LayoutItemData } from "../scenedata/behaviors/layoutitemdata";
import type { SceneObject } from "../core/SceneObject";
import type { TraceScene } from "../core/TraceScene";
// Type-only: erased at runtime, so this stays out of a cycle with layout.ts,
// which value-imports this module for the statics below.
import type { Layout, LayoutSizeProvider } from "./layout";
import { Logger } from "../utils/Logger";

export class LayoutItem extends TraceBehavior<LayoutItemData> {
    static readonly behaviorType = "LayoutItem" as const;

    // ── Standalone width authority (#709) ───────────────────────────────────
    //
    // `LayoutItem` is THE box authority, container or not: "text bounds" IS
    // `LayoutItem { width, widthMode: 'Fixed' }`. A width-accepting provider
    // (Text) on a node with no self-or-ancestor Layout wraps to that width
    // through the same `setLayoutResolvedWidth` channel a container would use,
    // so a freestanding paragraph is authorable without wrapping it in a box.
    //
    // These are statics rather than instance methods because the departure
    // sweep in layout.ts asks them about a node whose LayoutItem it does not
    // hold, and because the feed must run for a node that has no Layout to
    // route through.

    /**
     * The Layout that owns this node's box — nearest self-or-ancestor, or null
     * when nothing does. String-keyed lookup keeps this module acyclic.
     *
     * The identity matters, not just the existence: a container sweeping its
     * departures has to tell "this node is no longer mine" from "this node is
     * someone else's now", and only the second forbids handing the node back
     * its standalone entitlement.
     */
    static nearestLayoutAuthority(sceneObject: SceneObject): Layout | null {
        let obj: SceneObject | null = sceneObject;
        while (obj) {
            const layout = obj.getBehavior('Layout');
            if (layout) return layout;
            obj = obj.getParent();
        }
        return null;
    }

    /**
     * True when nothing between this node and the root manages it — the
     * condition under which the node's own `LayoutItem` is the only box
     * authority there is.
     */
    static hasNoLayoutAuthority(sceneObject: SceneObject): boolean {
        return LayoutItem.nearestLayoutAuthority(sceneObject) === null;
    }

    /**
     * The width this node is entitled to on its own: its `LayoutItem`'s Fixed
     * width or finite positive Hug maxWidth, otherwise null. A
     * non-positive width is a value, not an absence — see the presence note
     * below.
     *
     * Read RESOLVED, not authored, exactly as the container path reads sibling
     * sizing (`Layout._buildNode` → `_getResolvedData()`): every field can be
     * authored as `"{cardWidth}"`, and authored data still holds the ref
     * string. Reading it authored made the same configuration wrap inside a
     * container and silently not wrap outside one.
     *
     * Configuration only, never a measurement — which is what keeps the
     * standalone feed out of the oscillation class flagged in #706: the
     * provider re-measures against a width it did not itself produce.
     *
     * Fill yields no constraint (there is no solver to resolve a share).
     * Hug yields its maxWidth, if any, as a wrapping ceiling. Fixed width
     * remains unclamped by min/max. A standalone Fixed HEIGHT does nothing —
     * text owns its measured height.
     *
     * A **presence** gate, not truthiness: an authored `width: 0` is a real
     * authored box on the container path (`_applyItemFields` reads it through
     * `??`), so collapsing it to null here would make this generically-named
     * entry point disagree with the solver about the same data. What a
     * non-positive width MEANS is the consumer's call — Text already declines
     * one (`_maxWidthUnits` guards `> 0`), so nothing changes today.
     */
    static standaloneWidth(sceneObject: SceneObject): number | null {
        return LayoutItem._standaloneFeed(sceneObject).width;
    }

    /**
     * An authored Hug maxWidth that acts as a wrapping ceiling: finite and
     * positive, else null. One rule for the container solve and the standalone
     * feed; `createLabel` (creator-tools/build/spatial-builder.ts) repeats it
     * because that layer imports behavior types only.
     */
    static hugCeiling(maxWidth: unknown): number | null {
        return typeof maxWidth === 'number' && Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : null;
    }

    /**
     * `standaloneWidth` plus the slot that width imposes. A Hug maxWidth only
     * limits wrapping, so it is no slot for anchored text to align to; a
     * Fixed width is both.
     */
    private static _standaloneFeed(sceneObject: SceneObject): { width: number | null; slot: number | null } {
        const none = { width: null, slot: null };
        const item = sceneObject.getBehavior(LayoutItem);
        // Mid-detach exclusion, and it is load-bearing: `removeBehavior` runs
        // `detach()` BEFORE splicing the behavior out, so a dying LayoutItem
        // is still findable here. The retraction in `onDetach` unwraps the
        // text, which publishes a new measurement, which invalidates — and
        // without this the fallthrough would read the corpse's width and feed
        // it straight back, so removing the item never unwrapped anything.
        if (!item || item._detaching) return none;
        const data = item._getResolvedData();
        if (data.widthMode === 'Hug') {
            return { width: LayoutItem.hugCeiling(data.maxWidth), slot: null };
        }
        if (data.widthMode !== 'Fixed' || typeof data.width !== 'number') return none;
        return { width: data.width, slot: data.width };
    }

    /**
     * Push a node's standalone entitlement to every width-accepting provider
     * on it — the value a container's solve would otherwise feed.
     *
     * A no-op when a Layout has authority: that container owns the feed, and
     * writing here would fight its solve.
     *
     * `force` is not "do it anyway" — it is the caller asserting that whatever
     * authority `hasNoLayoutAuthority` can still see is not one that will feed
     * this node. Three callers, and they earn it differently:
     *
     * - `Layout._applyResults`' end-of-pass sweep and `Layout._releaseAll`:
     *   the authority they see is themselves, mid-pass or mid-teardown. That
     *   is a claim about WHICH layout the veto comes from, so both confirm it
     *   through `_ownsDeparture` first — a node that has since moved to
     *   another tree is being solved there, and forcing would overwrite a
     *   width it legitimately owns.
     * - `LayoutItem._notifyParentLayout`: no authority exists at all. It has
     *   just walked the ancestors and checked for a self-`Layout`, which is
     *   `hasNoLayoutAuthority` by another name, so `force` only skips
     *   re-walking what the caller established one line earlier.
     *
     * A fourth caller would need its own argument. "It seemed to work" is not
     * one — the failure is silent and lands on whichever container did own the
     * node.
     */
    static feedStandaloneWidth(sceneObject: SceneObject, force = false): void {
        if (!force && !LayoutItem.hasNoLayoutAuthority(sceneObject)) return;
        const { width, slot } = LayoutItem._standaloneFeed(sceneObject);
        LayoutItem._pushWidth(sceneObject, width, slot);
    }

    /**
     * Push a width to every width-accepting provider on a node — the one place
     * that knows how to find them, so the retract and the feed cannot drift.
     *
     * The cast is deliberate rather than `isLayoutSizeProvider`: that guard is
     * a VALUE in layout.ts, which value-imports this module for the statics
     * above, so importing it back would be a real cycle (the same constraint
     * that keeps every `Layout` lookup here string-keyed). Optional chaining
     * gives the same answer the guard would — a no-op on a behavior that does
     * not accept a width.
     */
    private static _pushWidth(sceneObject: SceneObject, width: number | null, slotWidth: number | null): void {
        for (const behavior of sceneObject.getBehaviors()) {
            (behavior as unknown as LayoutSizeProvider).setLayoutResolvedWidth?.(width, slotWidth);
        }
    }

    protected override onAttach(): void {
        LayoutItem._installReparentHook(this.sceneObject.traceScene);
        this._notifyParentLayout();
    }

    protected override async applyCommit(_patch: Partial<LayoutItemData>, previous: LayoutItemData): Promise<void> {
        // Authored commits pass a clone; property re-resolves pass the authored object itself.
        this._notifyParentLayout({ authored: previous !== this.getAuthored() });
    }

    /**
     * Scenes already carrying the reparent hook, so the subscription is one
     * per scene rather than one per LayoutItem — a populated scene has
     * hundreds of these, and every reparent would otherwise walk all of them.
     * Weakly keyed: the listener dies with the scene, which is also why it is
     * never explicitly removed.
     */
    private static readonly _reparentHooked = new WeakSet<TraceScene>();

    /**
     * Re-assert standalone widths across a reparented subtree.
     *
     * A reparent changes box AUTHORITY without invalidating anything:
     * `_notifyHierarchyChanged` re-resolves property refs and stops there, so
     * the container a node just left never re-solves and its departure sweep
     * never runs. Without this hook a paragraph dragged OUT of a container
     * keeps the container's last width forever.
     *
     * The joining side is not this hook's job, and it does not do it: by the
     * time the listener runs the node already reports its new parent, so
     * `hasNoLayoutAuthority` is false and the unforced feed returns without
     * writing. A node dragged INTO a container gets its width from that
     * container's own solve, whenever the host invalidates it — nothing in
     * `setParent` does, which is pre-existing and outside this behavior's
     * reach.
     *
     * Descendants are included because authority is inherited: moving a
     * subtree changes it for everything inside.
     */
    private static _installReparentHook(scene: TraceScene | undefined): void {
        // Not every host hands a behavior a full TraceScene — test harnesses
        // and partial embeddings supply less. The hook is an optimisation of
        // timing, not a correctness requirement, so decline quietly.
        if (!scene || typeof scene.onObjectReparented !== 'function') return;
        if (LayoutItem._reparentHooked.has(scene)) return;
        LayoutItem._reparentHooked.add(scene);
        scene.onObjectReparented((obj) => {
            for (const node of [obj, ...obj.getDescendants()]) {
                if (node.transform.isDisposed()) continue;
                if (!node.getBehavior(LayoutItem)) continue;
                LayoutItem.feedStandaloneWidth(node);
            }
        });
    }

    /** Set for the window between `detach()` and this behavior being spliced out. */
    private _detaching = false;

    /**
     * Whether the "nothing sizes it" warning already fired for the CURRENT
     * stretch of being unsized — cleared on every path that leaves that state,
     * a container taking the node over included. Tracking "was ever unsized"
     * instead would silence the warning for good once a node had been parked
     * under a Layout and dragged back out still unsized.
     */
    private _warnedNoWidth = false;

    protected override onDetach(): void {
        this._detaching = true;
        // The node keeps its providers but loses its box authority, so retract
        // the constraint and standalone text unwraps.
        //
        // Unforced, and that is the whole retraction: `feedStandaloneWidth`
        // makes the same authority check this used to make by hand, and
        // `standaloneWidth` already answers null while `_detaching` is set —
        // which is why the flag exists, since `removeBehavior` runs `detach()`
        // BEFORE splicing and the dying item is otherwise still findable.
        // Spelling it out here as a second check-and-push was another copy of
        // "work out the right width and push it", the shape that let the two
        // ancestor walks drift apart.
        LayoutItem.feedStandaloneWidth(this.sceneObject);

        // The node just left layout management — re-solve so the departure
        // sweep runs promptly (receiver told to drop its slot box, claims
        // retracted, arrangement re-flowed) instead of waiting for an
        // unrelated edit. Quiet: detaching alongside the whole container
        // during teardown is not warn-worthy.
        this._invalidateAncestorLayout();
    }

    /**
     * Invalidate the nearest ANCESTOR container, and say whether there was one.
     *
     * Deliberately starts at the parent: a node's own Layout arranges its
     * CHILDREN, while this LayoutItem describes how the node sits in its
     * parent, so the two are never the same pass. One copy, because this walk
     * being subtly different in two places is exactly the defect this PR
     * already fixed once.
     */
    private _invalidateAncestorLayout(options: { authored?: boolean } = {}): boolean {
        let obj = this.sceneObject.getParent();
        while (obj) {
            const layout = obj.getBehavior('Layout');
            if (layout) { layout.invalidate(options); return true; }
            obj = obj.getParent();
        }
        return false;
    }

    private _notifyParentLayout(options: { authored?: boolean } = {}): void {
        // A walk that started at the NODE and returned on the first Layout it
        // found silently stopped at a nested container's own Layout and never
        // notified the page above it — a width commit on a card with internal
        // auto-layout did nothing to the grid holding it.
        if (this._invalidateAncestorLayout(options)) { this._warnedNoWidth = false; return; }
        // No container above. A node with its own Layout is still not
        // standalone — that Layout sizes it — so there is nothing to feed.
        // Nothing re-solves it from here either: a root container's own width
        // commit is not re-run by this path (unchanged from before; a Layout
        // re-solves on its own data, not on its LayoutItem's).
        if (this.sceneObject.getBehavior('Layout')) { this._warnedNoWidth = false; return; }

        // Past both returns, "no ancestor Layout and no Layout of its own" is
        // established — which is `hasNoLayoutAuthority` spelled out. `force`
        // therefore changes nothing here; it skips re-walking the ancestors
        // one line after that walk finished. Unforced would behave the same.
        //
        // It is also the precondition the warning below states out loud, so
        // the two returns above have to keep coming first: reorder them and
        // the message describes a node it is no longer talking about.
        //
        // This LayoutItem is the box authority (#709), so feed the width
        // rather than warning about a missing parent: a Fixed width on
        // standalone text is now a supported configuration. The warn survives
        // only for a LayoutItem that gives a standalone node nothing at all —
        // strictly narrower than the one it replaces, which fired for every
        // node whose direct parent lacked a Layout.
        LayoutItem.feedStandaloneWidth(this.sceneObject, true);
        // A Hug ceiling only sizes a node that has something to wrap; on any
        // other node it is as unsized as before.
        const wraps = this.sceneObject.getBehaviors().some(
            b => typeof (b as unknown as LayoutSizeProvider).setLayoutResolvedWidth === 'function');
        const sized = LayoutItem.standaloneWidth(this.sceneObject) !== null
            && (wraps || this._getResolvedData().widthMode === 'Fixed');
        if (sized) {
            this._warnedNoWidth = false;
            return;
        }
        // Once per stretch of the condition, not once per commit: this runs on
        // every `applyCommit`, and a Hug/Fill item parked outside a container
        // is a legitimate in-progress authoring state. Re-arms if the author
        // fixes it and breaks it again.
        if (this._warnedNoWidth) return;
        this._warnedNoWidth = true;
        Logger.warn(
            `[LayoutItem] "${this.sceneObject.name}" has a LayoutItem with neither a Fixed width `
            + 'nor a Hug maxWidth, and no ancestor Layout — nothing sizes it.',
        );
    }
}
